import * as THREE from 'three/webgpu';
import {
  attribute,
  billboarding,
  float,
  instancedBufferAttribute,
  mix,
  oneMinus,
  positionGeometry,
  rotate,
  step,
  texture,
  uniform,
  uv,
  varying,
  vec2,
  vec3,
} from 'three/tsl';

import { uNight } from '../core/uniforms.js';
import { warmGrade, hitFlashMix, weatherGrade } from './nodes.js';

/**
 * @file `SpriteBatch` — N camera-facing sprites in ONE draw call, with the SAME
 * API on both rendering backends. Grass, foliage cards, enemy impostors, damage
 * numbers, ghosts: anything that is a textured quad standing in the world.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO BACKENDS, ONE API
 * ─────────────────────────────────────────────────────────────────────────────
 * **WebGPU** (`caps.instancedSprite === true`): one `THREE.Sprite` with
 * `.count = N` and a `SpriteNodeMaterial`. Per-instance data can ONLY reach the
 * shader through nodes — `positionNode` / `scaleNode` / `rotationNode` fed by
 * `instancedBufferAttribute()`. This path is WebGPU-only: the WebGL2 backend
 * ignores `Sprite.count`.
 *
 * **WebGL2 fallback**: a `THREE.Mesh` over an `InstancedBufferGeometry` (a unit
 * plane) with `material.vertexNode = billboarding(...)`. Deliberately NOT an
 * `InstancedMesh`: `NodeMaterial.setupPosition()` applies `instanceMatrix` to
 * `positionLocal` BEFORE `vertexNode` runs, so a matrix-driven InstancedMesh
 * would hand `billboarding()` an already-translated vertex and rotate the entire
 * batch about the object origin. Driving everything from instanced ATTRIBUTES
 * (`iPos` etc.) side-steps that entirely, costs 16 floats/instance less, and
 * keeps both paths reading the exact same buffers. `geometry.instanceCount` is
 * the draw count here; `Sprite.count` is the draw count there — `commit()` hides
 * the difference.
 *
 * ⚠ DEVIATION from the T5 brief, verified in the browser on both backends:
 *
 * 1. `billboarding({ vertical: true })`, not `false`. With `vertical:false` the
 *    quad's up axis stays WORLD up, so at the game's 52-degree pitch every
 *    sprite renders ~62% (cos 52) of its authored height and leans away — while
 *    the WebGPU `Sprite` path is fully screen-aligned. The two backends would
 *    not match, and T8's impostors (baked AT the game pitch) would be
 *    double-foreshortened. `billboardVertical: false` is still an option for
 *    ground-rooted cards.
 * 2. `flipX` mirrors the FRAME'S UVs, it does not mirror the quad. A negative
 *    scale.x reverses triangle winding, and both materials cull back faces, so
 *    "negative scale.x" makes sprites VANISH rather than flip. The sign of
 *    `iScale.x` still carries the flip (as specced) — the shader reads
 *    `abs(iScale.x)` for the width and `step(iScale.x, 0)` for the mirror.
 *
 * Both paths set `frustumCulled = false` (an instanced batch has no meaningful
 * object-space bounds; the Sprite path would otherwise cull against the object
 * ORIGIN and pop the whole batch out at the screen edge) and get a generous
 * bounding sphere for anything that still asks.
 *
 * ⚠ Per-instance positions are treated as WORLD space: add `object` to the scene
 * or to an untransformed group. A rotated/translated parent means the two
 * backends disagree (the Sprite path multiplies by the model-view matrix, the
 * fallback discards the parent's rotation by construction).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DEPTH BIAS — why a billboard is cut in half by the wall BEHIND it
 * ─────────────────────────────────────────────────────────────────────────────
 * A screen-aligned quad is perpendicular to the view axis, so EVERY fragment of
 * it carries ONE depth: the depth of its anchor, which `center.set(0.5,
 * anchorY)` puts at the actor's FEET. The camera is pitched 52 degrees down, so
 * a wall 40 cm BEHIND an actor, sampled at head height, is genuinely NEARER the
 * camera than that actor's feet are — and wins the depth test against the
 * sprite's upper pixels. The actor is drawn cut off at the waist, and a
 * shopkeeper behind a 1.05 m counter reads as a body lying on it. The deficit
 * is `h * sin(pitch)`: 1.02 m for a 1.3 m chibi at 52 degrees, which is exactly
 * where the head measured clean (clipped at 0.40 m, clean at 1.30 m).
 *
 * {@link SpriteBatch#setDepthBias} translates each instance ALONG THE VIEW AXIS,
 * toward the camera, by `iScale.y` (the instance's own world height) times the
 * pre-scaled vector the caller passes — {@link cameraDepthBias} is that vector.
 * Because the translation is along the view axis the ON-SCREEN image is
 * essentially unchanged; only the depth moves, so an actor lifts out of the
 * geometry behind them without looking any different, and a knee-high prop in
 * the same batch gets proportionally almost nothing.
 *
 * ⚠ THE TRADE-OFF, and it is real rather than a defect: biasing toward the
 * camera also lets an actor draw IN FRONT of geometry that should genuinely
 * occlude them — a wall BETWEEN the camera and the player. `OcclusionFade.js`
 * already dissolves the buildings that do that, and the interiors force their
 * camera-side walls, so the two systems overlap; how much of the compensation
 * is worth spending against how much see-through is a judgement call, which is
 * why the strength is ONE tunable number ({@link ACTOR_DEPTH_BIAS}) and not
 * baked into the shader.
 *
 * The bias is OFF (zero) by default: an untouched batch builds the same node
 * graph value it always did, and impostor batches — baked AT the game pitch,
 * see `render/ImpostorBaker.js` — must stay that way.
 *
 * @module engine/render/Billboards
 */

/** @typedef {import('../types.js').EngineCaps} EngineCaps */
/** @typedef {[number, number, number]} RGB */

/**
 * Per-instance layout. 14 floats — small enough that a 4096-sprite forest batch
 * is 224 KB and a full re-upload every frame is not worth optimising.
 * @type {ReadonlyArray<{name: string, size: number, type: string}>}
 */
const ATTRS = Object.freeze([
  { name: 'iPos', size: 3, type: 'vec3' },
  /** Signed: `x < 0` means "mirror this frame". Magnitude is the world size. */
  { name: 'iScale', size: 2, type: 'vec2' },
  { name: 'iFrame', size: 1, type: 'float' },
  { name: 'iRot', size: 1, type: 'float' },
  { name: 'iTintD', size: 3, type: 'vec3' },
  { name: 'iTintN', size: 3, type: 'vec3' },
  { name: 'iFlash', size: 1, type: 'float' },
]);

const WHITE = Object.freeze([1, 1, 1]);
/** Same default an un-baked instanced prop gets (`build/InstancedBatch.js`). */
const NIGHT_DEFAULT = Object.freeze([0.25, 0.3, 0.45]);

/**
 * THE ONE TUNING NUMBER for the depth bias (see the header). A FRACTION of the
 * full `h * sin(pitch)` compensation:
 *
 *  • `1.0` pays the whole deficit — an actor standing flat against a wall is
 *    never eaten by it, at the price of the most see-through when a wall is
 *    between the camera and the actor;
 *  • `0.5` lifts a 1.3 m chibi clear of anything within ~0.5 m of their back
 *    and halves that see-through;
 *  • `0` restores the pre-fix behaviour exactly (`x + 0 * h === x`).
 *
 * Only `systems/Gameplay.js` spends it, and only on the ACTOR batches.
 */
export const ACTOR_DEPTH_BIAS = 1.0;

/**
 * The per-batch half of the depth bias: the unit vector from the scene TOWARD
 * the camera, pre-scaled by `ACTOR_DEPTH_BIAS * sin(pitch)` so the shader has
 * only the per-instance height left to multiply in.
 *
 * The camera sits at `target + (sin yaw, ., cos yaw) * d` looking down by
 * `pitch` (`core/Engine.js#updateCamera`), so scene->camera is
 * `(sin yaw * cos pitch, sin pitch, cos yaw * cos pitch)` — a unit vector. Read
 * the rig for both angles: the pitch is a rig constant, and a second copy of 52
 * degrees is the kind of duplicate this codebase punishes.
 *
 * @param {number} yaw Radians, the rig's.
 * @param {number} pitch Radians below horizontal, the rig's.
 * @param {number} [strength=ACTOR_DEPTH_BIAS] Fraction of the full compensation.
 * @param {{x: number, y: number, z: number}} [out] Reused scratch.
 * @returns {{x: number, y: number, z: number}} Feed straight to `setDepthBias`.
 */
export function cameraDepthBias(yaw, pitch, strength = ACTOR_DEPTH_BIAS, out = { x: 0, y: 0, z: 0 }) {
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  // `sp` twice on purpose: once for the camera's own elevation, once because
  // the depth a screen-aligned quad LOSES over its height is `h * sin(pitch)`.
  const k = strength * sp;
  out.x = Math.sin(yaw) * cp * k;
  out.y = sp * k;
  out.z = Math.cos(yaw) * cp * k;
  return out;
}

export class SpriteBatch {
  /**
   * @param {Object} o
   * @param {THREE.Texture} o.texture Sprite atlas. The CALLER owns its filtering
   *   (`assets/playerSprite.js -> configureSpriteTexture` is the house style) and
   *   its disposal unless `ownsTexture` is set.
   * @param {number} [o.cols=1] Atlas columns.
   * @param {number} [o.rows=1] Atlas rows. Frame 0 is TOP-LEFT, then row-major.
   * @param {number} o.max Hard instance cap; buffers are allocated up front.
   * @param {EngineCaps} [o.caps] Live caps object from the Engine. Only
   *   `instancedSprite` is read, at construction time (by then `init()` has
   *   resolved it).
   * @param {number} [o.worldHeight=1] Height of ONE frame in metres.
   * @param {number} [o.aspect] Frame width/height. Defaults to the atlas image's
   *   frame aspect, or 1 when the image is not loaded yet.
   * @param {number} [o.anchorY=0] Fraction of the sprite BELOW the anchor point.
   *   0 = stand on the anchor (the default — billboards must have their feet
   *   exactly at the sampled ground height, or they z-fight the terrain),
   *   0.5 = centred.
   * @param {number} [o.alphaTest=0.5] 0.5 for characters, ~0.1 for foliage. Set
   *   to 0 for genuinely blended sprites (and then set `transparent`).
   * @param {boolean} [o.transparent=false] Cutout by default: alpha-tested
   *   silhouettes depth-write and need no sorting.
   * @param {boolean} [o.depthWrite=true]
   * @param {boolean} [o.nightMix=true] Blend `tintD`/`tintN` by `uNight`. Off =
   *   `tintD` only (UI markers, debug gizmos).
   * @param {boolean} [o.sizeAttenuation=true] Off = constant screen size.
   * @param {boolean} [o.billboardVertical=true] Fallback path only; see the
   *   header note. `false` gives a ground-rooted (cylindrical) card.
   * @param {boolean} [o.forceFallback=false] Use the InstancedBufferGeometry path
   *   even on WebGPU — the only way to A/B the two on one machine.
   * @param {boolean} [o.ownsTexture=false] Dispose the texture in `dispose()`.
   * @param {number} [o.boundingRadius=1e5]
   * @param {string} [o.name='spriteBatch']
   */
  constructor(o = {}) {
    const {
      texture: map = null,
      cols = 1,
      rows = 1,
      max = 256,
      caps = null,
      worldHeight = 1,
      aspect = null,
      anchorY = 0,
      alphaTest = 0.5,
      transparent = false,
      depthWrite = true,
      nightMix = true,
      sizeAttenuation = true,
      billboardVertical = true,
      forceFallback = false,
      ownsTexture = false,
      boundingRadius = 1e5,
      name = 'spriteBatch',
    } = o;

    if (!(max > 0)) throw new Error('SpriteBatch: `max` must be > 0');

    /** @type {number} */
    this.max = max | 0;
    /** @type {number} Active instances — what `commit()` uploads and draws. */
    this.count = 0;
    /** @type {number} */
    this.cols = Math.max(1, cols | 0);
    /** @type {number} */
    this.rows = Math.max(1, rows | 0);
    /** @type {number} */
    this.worldHeight = worldHeight;
    /** @type {number} */
    this.anchorY = anchorY;
    /** @type {THREE.Texture|null} */
    this.texture = map;
    /** @type {'sprite'|'mesh'} Which backend path this instance took. */
    this.mode = !forceFallback && caps?.instancedSprite === true ? 'sprite' : 'mesh';

    /** Frame aspect, from the atlas image unless the caller overrode it. */
    this.aspect = aspect ?? frameAspect(map, this.cols, this.rows);

    this._ownsTexture = ownsTexture;
    this._overflow = 0;
    this._disposed = false;

    // ── per-instance buffers (the single source of truth) ────────────────────
    /** @type {Object<string, Float32Array>} */
    this.data = {};
    for (const a of ATTRS) this.data[a.name] = new Float32Array(this.max * a.size);
    // A zero scale is an invisible quad, which is what an untouched slot must be.
    this.data.iTintD.fill(1);

    /** @type {Object<string, any>} TSL node per attribute. */
    this._nodes = {};
    /** @type {Array<{needsUpdate: boolean}>} Objects to flag in `commit()`. */
    this._dirty = [];
    /**
     * Pre-scaled camera-toward vector; see the header and {@link setDepthBias}.
     * ZERO until somebody opts in, and zero is the identity: the shader adds
     * `bias * iScale.y` to the instance position, and `p + 0 * h` is `p`.
     */
    this._uDepthBias = uniform(new THREE.Vector3(0, 0, 0));

    if (this.mode === 'sprite') this._buildSpritePath({ alphaTest, transparent, depthWrite, nightMix, sizeAttenuation, name, boundingRadius });
    else this._buildMeshPath({ alphaTest, transparent, depthWrite, nightMix, billboardVertical, name, boundingRadius });
  }

  // ── writing ────────────────────────────────────────────────────────────────

  /**
   * Writes one instance. Everything is optional except the position.
   *
   * @param {number} i Slot index, `0 .. max-1`. Slots are stable: writing slot 7
   *   every frame animates instance 7.
   * @param {number} x @param {number} y World position of the ANCHOR (with the
   *   default `anchorY = 0` that is where the sprite's feet touch the ground).
   * @param {number} z
   * @param {Object} [opts]
   * @param {number} [opts.scale=1] Multiplier on `worldHeight`.
   * @param {number} [opts.frame=0] Atlas frame index (wraps).
   * @param {number} [opts.rot=0] In-plane (screen) rotation, radians, about the
   *   anchor. This is the hook `WalkWobble.tilt` plugs into for instanced actors.
   * @param {RGB|{r:number,g:number,b:number}} [opts.tintD] Day tint (default white).
   * @param {RGB|{r:number,g:number,b:number}} [opts.tintN] Night tint (default
   *   `[0.25, 0.3, 0.45]`, matching un-baked instanced props).
   * @param {number} [opts.flash=0] 0..1 crossfade toward white.
   * @param {boolean} [opts.flipX=false] Mirror horizontally (art faces right).
   * @returns {number} `i`, or `-1` when out of range.
   */
  set(i, x, y, z, opts) {
    if (i < 0 || i >= this.max) {
      if (this._overflow++ === 0) {
        console.warn(`[SpriteBatch:${this.name}] slot ${i} is outside 0..${this.max - 1}; write dropped`);
      }
      return -1;
    }

    const scale = opts?.scale ?? 1;
    const h = this.worldHeight * scale;
    const w = h * this.aspect;

    const p = this.data.iPos;
    const p3 = i * 3;
    p[p3] = x;
    p[p3 + 1] = y;
    p[p3 + 2] = z;

    const s = this.data.iScale;
    const s2 = i * 2;
    s[s2] = opts?.flipX ? -w : w;
    s[s2 + 1] = h;

    this.data.iFrame[i] = opts?.frame ?? 0;
    this.data.iRot[i] = opts?.rot ?? 0;
    this.data.iFlash[i] = opts?.flash ?? 0;

    writeRGB(opts?.tintD, this.data.iTintD, p3, WHITE);
    writeRGB(opts?.tintN, this.data.iTintN, p3, NIGHT_DEFAULT);

    if (i >= this.count) this.count = i + 1;
    return i;
  }

  /**
   * Hides one slot without shrinking `count` (its neighbours keep their indices).
   * @param {number} i
   */
  hide(i) {
    if (i < 0 || i >= this.max) return;
    this.data.iScale[i * 2] = 0;
    this.data.iScale[i * 2 + 1] = 0;
  }

  /**
   * Drops the active count to zero. The usual per-frame pattern for a dynamic
   * batch is `clear()` -> `set()` x N -> `commit()`; stale slots above the new
   * count are simply not drawn, so there is nothing to erase.
   */
  clear() {
    this.count = 0;
  }

  /**
   * Opts this batch into the depth bias — see the header for what it buys and
   * what it costs. Cheap enough to call every frame (one uniform write, no
   * upload): {@link cameraDepthBias} turns the rig's yaw/pitch into the vector,
   * and the shader multiplies it by each instance's own world height, so one
   * call covers a whole population of different-sized actors.
   *
   * ⚠ Instance DATA is untouched — this is draw state only. `iPos` still reads
   * back exactly what {@link set} wrote, which is what keeps every hit test,
   * blob shadow and blip anchored to where the actor actually stands.
   *
   * @param {number} [x] @param {number} [y] @param {number} [z] World-space
   *   metres per metre of instance height. All-zero (the default) restores the
   *   un-biased batch exactly.
   */
  setDepthBias(x = 0, y = 0, z = 0) {
    this._uDepthBias.value.set(x, y, z);
  }

  /** Uploads whatever changed and syncs the draw count. Call once per frame. */
  commit() {
    if (this._disposed) return;
    for (let i = 0; i < this._dirty.length; i++) this._dirty[i].needsUpdate = true;
    if (this.mode === 'sprite') {
      /** @type {any} */ (this.object).count = this.count;
    } else {
      /** @type {any} */ (this.object).geometry.instanceCount = this.count;
    }
    /** @type {any} */ (this.object).visible = this.count > 0;
  }

  /** @returns {{count: number, max: number, mode: string, dropped: number}} */
  stats() {
    return { count: this.count, max: this.max, mode: this.mode, dropped: this._overflow };
  }

  /** Idempotent. Frees the geometry/material (and the texture if it owns it). */
  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.object?.removeFromParent?.();
    // The Sprite path borrows three's shared module-level quad geometry — never
    // dispose that. The fallback owns its InstancedBufferGeometry.
    if (this.mode === 'mesh') /** @type {any} */ (this.object)?.geometry?.dispose?.();
    this.material?.dispose?.();
    if (this._ownsTexture) this.texture?.dispose?.();
    this._dirty.length = 0;
  }

  // ── backends ───────────────────────────────────────────────────────────────

  /**
   * The instance's world position with the depth bias applied — ONE expression,
   * built once and handed to whichever backend this batch took, so the two can
   * never disagree about where a biased sprite sits.
   *
   * `iScale.y` is the instance's world HEIGHT (`worldHeight * scale`), so the
   * bias is per-instance for free: a 1.8 m pedestrian gets 1.4x what a 1.3 m
   * chibi gets, and a hidden slot (scale 0) gets none.
   * @returns {any} vec3
   */
  _biasedPos() {
    return this._nodes.iPos.add(this._uDepthBias.mul(this._nodes.iScale.y));
  }

  /**
   * WebGPU: `Sprite` + `SpriteNodeMaterial`, per-instance data via
   * `instancedBufferAttribute()` nodes.
   * @param {any} cfg
   */
  _buildSpritePath(cfg) {
    // `instancedBufferAttribute(interleavedBuffer, ...)` keeps the InterleavedBuffer
    // WE created, so `needsUpdate` has an owner. Handing it a raw array instead
    // makes three mint an InterleavedBuffer internally at build time, and there
    // is then no reachable object to flag when the data changes.
    for (const a of ATTRS) {
      const buf = new THREE.InstancedInterleavedBuffer(this.data[a.name], a.size, 1);
      this._nodes[a.name] = instancedBufferAttribute(buf, a.type, a.size, 0);
      this._dirty.push(buf);
    }

    const mat = new THREE.SpriteNodeMaterial();
    mat.name = cfg.name;
    mat.sizeAttenuation = cfg.sizeAttenuation;
    this._applyShading(mat, cfg);

    const sc = this._nodes.iScale;
    mat.positionNode = this._biasedPos();
    // abs(): the sign is the mirror flag, not a negative size (see the header).
    mat.scaleNode = vec2(sc.x.abs(), sc.y);
    mat.rotationNode = this._nodes.iRot;

    const sprite = new THREE.Sprite(mat);
    sprite.name = cfg.name;
    // center.y == anchorY: 0 puts the pivot at the sprite's feet.
    sprite.center.set(0.5, this.anchorY);
    sprite.count = 0;
    sprite.frustumCulled = false;
    sprite.matrixAutoUpdate = false;
    sprite.updateMatrix();
    sprite.visible = false;

    /** @type {THREE.Sprite|THREE.Mesh} */
    this.object = sprite;
    /** @type {THREE.Material} */
    this.material = mat;
    this.name = cfg.name;
  }

  /**
   * WebGL2 (and anywhere `caps.instancedSprite` is false): unit plane +
   * `InstancedBufferGeometry` + `billboarding()` vertex node.
   * @param {any} cfg
   */
  _buildMeshPath(cfg) {
    const src = new THREE.PlaneGeometry(1, 1);
    const geo = new THREE.InstancedBufferGeometry();
    geo.name = cfg.name;
    geo.setIndex(src.getIndex());
    geo.setAttribute('position', src.getAttribute('position'));
    geo.setAttribute('normal', src.getAttribute('normal'));
    geo.setAttribute('uv', src.getAttribute('uv'));
    geo.instanceCount = 0;
    // Safe despite sharing attributes with `geo`: three only attaches its
    // dispose listener the first time a geometry is RENDERED, and `src` never is.
    src.dispose();

    for (const a of ATTRS) {
      const attr = new THREE.InstancedBufferAttribute(this.data[a.name], a.size);
      geo.setAttribute(a.name, attr);
      this._nodes[a.name] = attribute(a.name, a.type);
      this._dirty.push(attr);
    }
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), cfg.boundingRadius);

    const mat = new THREE.MeshBasicNodeMaterial();
    mat.name = cfg.name;
    this._applyShading(mat, cfg);

    const sc = this._nodes.iScale;
    // Local quad, anchored and scaled, THEN rotated in-plane. Because the vertex
    // node below screen-aligns the quad, this rotation is a screen rotation —
    // identical to `SpriteNodeMaterial.rotationNode` on the other path.
    const qx = positionGeometry.x.mul(sc.x.abs());
    const qy = positionGeometry.y.add(float(0.5 - this.anchorY)).mul(sc.y);
    const q = rotate(vec2(qx, qy), this._nodes.iRot);
    mat.positionNode = vec3(q.x, q.y, 0);
    mat.vertexNode = billboarding({
      position: this._biasedPos(),
      horizontal: true,
      vertical: cfg.billboardVertical,
    });

    const mesh = new THREE.Mesh(geo, mat);
    mesh.name = cfg.name;
    mesh.frustumCulled = false;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    mesh.visible = false;

    /** @type {THREE.Sprite|THREE.Mesh} */
    this.object = mesh;
    /** @type {THREE.Material} */
    this.material = mat;
    this.name = cfg.name;
  }

  /**
   * The fragment half — identical on both paths, which is the point.
   * `atlas frame * mix(tintD*warm, tintN, uNight)` then the hit flash.
   * @param {THREE.Material & {colorNode?: any, opacityNode?: any}} mat
   * @param {any} cfg
   */
  _applyShading(mat, cfg) {
    mat.transparent = cfg.transparent;
    mat.depthWrite = cfg.depthWrite;
    mat.depthTest = true;
    mat.fog = true;
    // See render/nodes.js: `vertexColors:true` would multiply colorNode by the
    // `color` attribute a second time.
    mat.vertexColors = false;
    if (cfg.alphaTest > 0) mat.alphaTest = cfg.alphaTest;

    const flipT = step(this._nodes.iScale.x, float(0)); // 1 when scale.x <= 0
    const base = uv();
    const localUv = vec2(mix(base.x, oneMinus(base.x), flipT), base.y);
    const frameUv = spriteFrameUV(this.cols, this.rows, this._nodes.iFrame, localUv);

    const tex = this.texture ? texture(this.texture, frameUv) : null;

    // ⚠ `weatherGrade` by hand: a sprite blends a per-instance AMBIENT tint, not
    // a baked vertex colour, so it never touches `dayNightVertexColor` — the one
    // place the cloud/lightning grade otherwise lives. Without this a storm
    // desaturates the city and leaves every pedestrian in full sunlight.
    const tint = weatherGrade(
      cfg.nightMix
        ? mix(this._nodes.iTintD.mul(warmGrade()), this._nodes.iTintN, uNight)
        : this._nodes.iTintD.mul(warmGrade()),
    );

    let color = tex ? tex.rgb.mul(tint) : vec3(tint);
    color = hitFlashMix(this._nodes.iFlash, color);

    mat.colorNode = color;
    if (tex) mat.opacityNode = tex.a;
  }
}

/**
 * Atlas frame -> UV. Frame 0 is the TOP-LEFT cell, counting left-to-right then
 * downward (how every atlas packer and every human lays a sheet out), which is
 * why the row term is flipped: UV `v = 0` is the BOTTOM of a GL texture.
 *
 * Kept local (three's `spritesheetUV` numbers rows the other way and takes the
 * count as a node) so both backends and the unit tests agree on one convention.
 *
 * ⚠ The integer decode MUST stay inside `varying()` — that is the whole fix for
 * the multi-row bug, and it is not an optimisation. `frameNode` is a per-INSTANCE
 * attribute: read it in the fragment stage and three inserts a perspective-
 * interpolated varying, so a constant 15.0 arrives as 14.999999 and `floor()`
 * drops a whole frame. Both backends do this (measured on WebGPU *and* the WebGL2
 * mesh fallback — they were wrong identically), and because the error tracks the
 * quad's depth, WHICH frames break changes as the camera moves. Single-row
 * atlases hid it: an off-by-one there is the neighbouring column of the same row,
 * or frame 0 which cannot round down. Wrapping the maths in `varying()` evaluates
 * it in the VERTEX stage, where the attribute is still exact, and interpolates
 * only the resulting cell offset — where 1e-7 of a UV is invisible.
 *
 * @param {number} cols
 * @param {number} rows
 * @param {any} frameNode Per-instance float.
 * @param {any} uvNode vec2 in 0..1 over the whole quad.
 * @returns {any} vec2
 */
export function spriteFrameUV(cols, rows, frameNode, uvNode) {
  if (cols <= 1 && rows <= 1) return uvNode;
  const total = cols * rows;
  const f = frameNode.floor().mod(float(total));
  const col = f.mod(float(cols));
  const row = f.div(float(cols)).floor();
  // vec2(column, flipped row) — one varying, decoded per vertex, not per fragment.
  const cell = varying(vec2(col, float(rows - 1).sub(row)), 'spriteFrameCell');
  return vec2(
    uvNode.x.add(cell.x).div(float(cols)),
    uvNode.y.add(cell.y).div(float(rows)),
  );
}

/**
 * CPU mirror of {@link spriteFrameUV} — the rect a frame occupies in UV space.
 * Used by tests and by anything that needs to slice the atlas on the CPU
 * (`ImpostorBaker` verification, editor previews).
 *
 * @param {number} cols
 * @param {number} rows
 * @param {number} frame
 * @returns {{u0: number, v0: number, u1: number, v1: number}}
 */
export function frameUVRect(cols, rows, frame) {
  const total = Math.max(1, cols * rows);
  const f = ((Math.floor(frame) % total) + total) % total;
  const col = f % cols;
  const row = Math.floor(f / cols);
  return {
    u0: col / cols,
    v0: (rows - 1 - row) / rows,
    u1: (col + 1) / cols,
    v1: (rows - row) / rows,
  };
}

/**
 * @param {THREE.Texture|null} map
 * @param {number} cols
 * @param {number} rows
 * @returns {number} Aspect of ONE frame, or 1 when the image is not decoded yet.
 */
function frameAspect(map, cols, rows) {
  const img = /** @type {any} */ (map)?.image;
  const w = img?.width ?? 0;
  const h = img?.height ?? 0;
  if (!(w > 0 && h > 0)) return 1;
  return (w / cols) / (h / rows);
}

/**
 * @param {RGB|{r:number,g:number,b:number}|null|undefined} c
 * @param {Float32Array} dst
 * @param {number} off
 * @param {ReadonlyArray<number>} fallback
 */
function writeRGB(c, dst, off, fallback) {
  if (!c) {
    dst[off] = fallback[0];
    dst[off + 1] = fallback[1];
    dst[off + 2] = fallback[2];
    return;
  }
  if (Array.isArray(c)) {
    dst[off] = c[0];
    dst[off + 1] = c[1];
    dst[off + 2] = c[2];
    return;
  }
  dst[off] = /** @type {any} */ (c).r;
  dst[off + 1] = /** @type {any} */ (c).g;
  dst[off + 2] = /** @type {any} */ (c).b;
}
